import { takeEvery, put, select } from 'redux-saga/effects';

import { makePostRequest, makePostRequestDEPRECATED } from 'tools/utilities/ajax';
import {
  ISubmitData, ICompanyDetails, CallbackTypes,
} from 'types.d';
// eslint-disable-next-line import/no-cycle
import { getBearerToken } from 'store/modules/utils/getBearerToken';
import {
  TYPES,
  ISubmit,
  ISubmitSuccess,
  ISubmitFail,
  ISilentSubmitSuccess,
  ISilentSubmitFail,
  ISilentContactDetailsSubmitSuccess,
  ISilentContactDetailsSubmitFail,
  ISilentContactDetailsSubmit,
  ISilentSubmit,
} from './actions';
// eslint-disable-next-line import/no-cycle
import { getCompanyDetailsData } from '../sign-up/selectors';
import { uniqueGUID } from '../app/constants';
// eslint-disable-next-line import/no-cycle
import { getLeadChannels } from '../options/selectors';
import { CRMSimpleItem } from '../options/crmTypes';

type CallbackType = {
  name: string;
  id: string;
}

// 1. Full submission – when the customer fills out the entire form (Lead Channel = Online Sign Up Form)
// 2. Partial submission – when the customer exits the web form without submitting but has captured sufficient details to create a lead (Lead Channel = Online Silent Lead)
// 3. Call back request – when the customer has filled out and submitted the request callback from (Lead Channel = Online Call Back Request)
const CALLBACK_TYPES = {
  onlineCallBackRequest: { name: 'Online Call Back Request', id: '8c4f2c16-4dc6-ea11-a812-000d3acb08cf' },
  // onlineContactUsForm: { name: 'Online Contact Us Form', id: 'a2b34237-50d1-ea11-a813-000d3a6a398b' },
  // onlineSignUpForm: { name: 'Online Sign Up Form', id: '9ab86c1e-4dc6-ea11-a812-000d3acb08cf' },
  // onlineSilentLead: { name: 'Online Silent Lead', id: 'e516613d-50d1-ea11-a813-000d3a6a398b' },
};

interface CRMCallbackPayload {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  companyName: string;
  leadChannel: CallbackType;
  notes: string;
  requestId: string;
}

function transformCallbackToCRMPayload(leadChannel: CallbackType, data: ISubmitData): CRMCallbackPayload {
  let notes = data.isAccountant ? '**IS ACCOUNTANT**  \n  ' : '';
  notes += data.comment || '';

  return {
    leadChannel,
    firstName: data.firstName,
    lastName: data.lastName || '',
    email: data.email,
    phoneNumber: data.phone,
    companyName: '',
    notes,
    requestId: uniqueGUID,
  };
}

// function transformSilentCallbackToCRMPayload(leadChannel: CallbackType, data: ISilentSubmitData): CRMCallbackPayload {
//   let notes = data.isAccountant ? '**IS ACCOUNTANT**  \n  ' : '';
//   notes += data.comment || '';

//   const firstName = data.accountantName || data.firstName || '';
//   const lastName = data.lastName || '';
//   const email = data.email || '';
//   const phoneNumber = data.accountantPhoneNumber || data.phone || '';
//   const companyName = data.accountantBusinessName || '';

//   return {
//     leadChannel,
//     firstName,
//     lastName,
//     email,
//     phoneNumber,
//     companyName,
//     notes,
//     requestId: uniqueGUID,
//   };
// }

function* submitCallbackRequest(action: ISubmit) {
  try {
    const companyDetails: (ICompanyDetails | undefined) = yield select(getCompanyDetailsData);
    const companyName: string = companyDetails?.companyName || '';

    const bearerToken: string = yield select(getBearerToken);

    const callbackChannels: CRMSimpleItem[] = yield select(getLeadChannels) || [];
    const callbackChannel = callbackChannels.find(({ name }) => name === 'Online Call Back Request') || CALLBACK_TYPES.onlineCallBackRequest;

    // Online Call Back Request – for when the user has clicked on the ‘Request callback’ button and filled out their details
    yield makePostRequest('/api/Lead/Callback', transformCallbackToCRMPayload(callbackChannel, { ...action.payload, companyName }), bearerToken);

    yield put<ISubmitSuccess>({
      type: TYPES.SUBMIT_SUCCESS,
      payload: {
        success: true,
      },
    });
  } catch (error) {
    console.error(error);
    yield put<ISubmitFail>({
      type: TYPES.SUBMIT_FAIL,
      payload: {
        error: 'Something went wrong.',
      },
    });
  }
}

// =====================
// Silent submit (used in the CallbackRequestModal if the user has filled out details but doesn't submit)
// =====================
function* silentSubmitCallbackRequest(action: ISilentSubmit) {
  const data = {
    ...action.payload,
    // Add didNotSubmit flag so the server handler knows that it's a silent request callback
    type: CallbackTypes.SILENT,
  };

  try {
    yield makePostRequestDEPRECATED('/request-callback', data);

    yield put<ISilentSubmitSuccess>({
      type: TYPES.SILENT_SUBMIT_SUCCESS,
      payload: {
        hasSubmittedSilent: true,
      },
    });
  } catch (error) {
    yield put<ISilentSubmitFail>({
      type: TYPES.SILENT_SUBMIT_FAIL,
      payload: null,
    });
  }
}

// =====================
// Silent submit (used in the ContactDetails page if the user has filled out details and moves to the next step)
// =====================
function* silentSubmitContactDetails(action: ISilentContactDetailsSubmit) {
  // Add didNotSubmit flag so the server handler knows that it's a silent request callback
  const data = {
    ...action.payload,
    type: CallbackTypes.CONTACT_SILENT,
  };

  try {
    yield makePostRequestDEPRECATED('/request-callback', data);

    yield put<ISilentContactDetailsSubmitSuccess>({
      type: TYPES.SILENT_CONTACT_DETAILS_SUBMIT_SUCCESS,
      payload: {
        hasSubmittedContactDetailsSilent: true,
      },
    });
  } catch (error) {
    yield put<ISilentContactDetailsSubmitFail>({
      type: TYPES.SILENT_CONTACT_DETAILS_SUBMIT_FAIL,
      payload: null,
    });
  }
}

export function* callbackRequestSagas() {
  yield takeEvery(TYPES.SUBMIT, submitCallbackRequest);
  yield takeEvery(TYPES.SILENT_SUBMIT, silentSubmitCallbackRequest);
  yield takeEvery(TYPES.SILENT_CONTACT_DETAILS_SUBMIT, silentSubmitContactDetails);
}
